import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Zap,
  Lock,
  LogOut,
  Plus,
  Trash2,
  Save,
  ArrowLeft,
  ShieldCheck,
  Loader2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import {
  adminLogin,
  verifyAdmin,
  fetchWallets,
  createWallet,
  updateWallet,
  deleteWallet,
  setToken,
  clearToken,
  getToken,
} from "../lib/api";

const LoginScreen = ({ onSuccess }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const token = await adminLogin(password);
      setToken(token);
      toast({ title: "Access granted", description: "Welcome back, admin." });
      onSuccess();
    } catch (err) {
      setError("Incorrect password. Access denied.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a101e] bg-grid flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-slate-400 hover:text-yellow-400 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to site
        </button>

        <div className="bg-[#0f1a30] border border-yellow-400/30 rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-11 h-11 rounded-lg bg-[#0a101e] border border-yellow-400/30 flex items-center justify-center">
              <Lock className="w-5 h-5 text-yellow-400" />
            </span>
            <div>
              <div className="font-pixel text-yellow-400 text-sm">RIPS ADMIN</div>
              <div className="text-xs text-slate-400">Secure control panel</div>
            </div>
          </div>

          <p className="text-slate-400 text-sm mt-4 mb-6">
            Enter the admin password to manage crypto wallet addresses and coins.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-slate-300" htmlFor="pw">Password</Label>
              <Input
                id="pw"
                type="password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="mt-2 bg-[#0a101e] border-white/10 focus-visible:ring-yellow-400 text-slate-100"
              />
              {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
            </div>
            <Button
              type="submit"
              disabled={loading || !password}
              className="w-full h-11 bg-yellow-400 hover:bg-yellow-300 text-[#0a101e] font-semibold rounded-lg disabled:opacity-50 gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Unlock Panel
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

const WalletRow = ({ wallet, onSaved, onDeleted }) => {
  const { toast } = useToast();
  const [coin, setCoin] = useState(wallet.coin);
  const [symbol, setSymbol] = useState(wallet.symbol);
  const [address, setAddress] = useState(wallet.address);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const dirty = coin !== wallet.coin || symbol !== wallet.symbol || address !== wallet.address;

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateWallet(wallet.id, { coin, symbol, address });
      onSaved(updated);
      toast({ title: "Wallet updated", description: `${updated.coin} address saved.` });
    } catch (e) {
      toast({ title: "Update failed", description: "Please try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await deleteWallet(wallet.id);
      onDeleted(wallet.id);
      toast({ title: "Wallet removed", description: `${wallet.coin} deleted.` });
    } catch (e) {
      toast({ title: "Delete failed", description: "Please try again.", variant: "destructive" });
      setDeleting(false);
    }
  };

  return (
    <div className="bg-[#0a101e] border border-white/10 rounded-xl p-4">
      <div className="grid md:grid-cols-12 gap-3 items-end">
        <div className="md:col-span-4">
          <Label className="text-slate-400 text-xs">Coin name</Label>
          <Input value={coin} onChange={(e) => setCoin(e.target.value)}
            className="mt-1 bg-[#0f1a30] border-white/10 focus-visible:ring-yellow-400 text-slate-100" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-slate-400 text-xs">Symbol</Label>
          <Input value={symbol} onChange={(e) => setSymbol(e.target.value)}
            className="mt-1 bg-[#0f1a30] border-white/10 focus-visible:ring-yellow-400 text-slate-100" />
        </div>
        <div className="md:col-span-6">
          <Label className="text-slate-400 text-xs">Wallet address</Label>
          <Input value={address} onChange={(e) => setAddress(e.target.value)}
            className="mt-1 bg-[#0f1a30] border-white/10 focus-visible:ring-yellow-400 text-slate-100" />
        </div>
      </div>
      <div className="flex gap-2 mt-3 justify-end">
        <Button onClick={remove} disabled={deleting} variant="outline"
          className="h-9 bg-transparent border-red-500/40 text-red-400 hover:bg-red-500/10 rounded-lg gap-2">
          {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete
        </Button>
        <Button onClick={save} disabled={!dirty || saving}
          className="h-9 bg-yellow-400 hover:bg-yellow-300 text-[#0a101e] font-semibold rounded-lg gap-2 disabled:opacity-40">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </Button>
      </div>
    </div>
  );
};

const Dashboard = ({ onLogout }) => {
  const { toast } = useToast();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newCoin, setNewCoin] = useState({ coin: "", symbol: "", address: "" });

  const load = async () => {
    setLoading(true);
    try {
      setWallets(await fetchWallets());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newCoin.coin || !newCoin.symbol || !newCoin.address) return;
    setAdding(true);
    try {
      const created = await createWallet({ ...newCoin, order: wallets.length });
      setWallets((w) => [...w, created]);
      setNewCoin({ coin: "", symbol: "", address: "" });
      toast({ title: "Coin added", description: `${created.coin} is now available at checkout.` });
    } catch (e) {
      toast({ title: "Add failed", description: "Please try again.", variant: "destructive" });
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a101e]">
      <header className="border-b border-white/5 bg-[#0a101e]/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-lg bg-[#0f1a30] border border-yellow-400/30 flex items-center justify-center">
              <Zap className="w-5 h-5 text-yellow-400 fill-yellow-400" />
            </span>
            <span className="font-pixel text-yellow-400 text-sm tracking-wider">RIPS ADMIN</span>
          </div>
          <Button onClick={onLogout} variant="outline"
            className="h-9 bg-transparent border-white/15 text-slate-200 hover:bg-white/5 rounded-lg gap-2">
            <LogOut className="w-4 h-4" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-10">
        <h1 className="font-pixel pixel-white text-xl mb-2">CRYPTO WALLETS</h1>
        <p className="text-slate-400 mb-8">
          Update wallet addresses and manage accepted coins. Changes appear instantly at checkout.
        </p>

        <div className="bg-[#0f1a30] border border-yellow-400/30 rounded-2xl p-5 mb-8">
          <h2 className="text-yellow-400 font-semibold mb-4 flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add a new coin
          </h2>
          <div className="grid md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-4">
              <Label className="text-slate-400 text-xs">Coin name</Label>
              <Input placeholder="Solana" value={newCoin.coin}
                onChange={(e) => setNewCoin({ ...newCoin, coin: e.target.value })}
                className="mt-1 bg-[#0a101e] border-white/10 focus-visible:ring-yellow-400 text-slate-100" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-slate-400 text-xs">Symbol</Label>
              <Input placeholder="SOL" value={newCoin.symbol}
                onChange={(e) => setNewCoin({ ...newCoin, symbol: e.target.value })}
                className="mt-1 bg-[#0a101e] border-white/10 focus-visible:ring-yellow-400 text-slate-100" />
            </div>
            <div className="md:col-span-6">
              <Label className="text-slate-400 text-xs">Wallet address</Label>
              <Input placeholder="Wallet address" value={newCoin.address}
                onChange={(e) => setNewCoin({ ...newCoin, address: e.target.value })}
                className="mt-1 bg-[#0a101e] border-white/10 focus-visible:ring-yellow-400 text-slate-100" />
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <Button onClick={add} disabled={adding || !newCoin.coin || !newCoin.symbol || !newCoin.address}
              className="h-10 bg-yellow-400 hover:bg-yellow-300 text-[#0a101e] font-semibold rounded-lg gap-2 disabled:opacity-40">
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Coin
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> Loading wallets...</div>
        ) : (
          <div className="space-y-4">
            {wallets.map((w) => (
              <WalletRow
                key={w.id}
                wallet={w}
                onSaved={(u) => setWallets((list) => list.map((x) => (x.id === u.id ? u : x)))}
                onDeleted={(id) => setWallets((list) => list.filter((x) => x.id !== id))}
              />
            ))}
            {wallets.length === 0 && (
              <p className="text-slate-500 text-center py-8">No coins yet. Add one above.</p>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

const AdminPage = () => {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const check = async () => {
      if (!getToken()) { setChecking(false); return; }
      try {
        await verifyAdmin();
        setAuthed(true);
      } catch (e) {
        clearToken();
      } finally {
        setChecking(false);
      }
    };
    check();
  }, []);

  const logout = () => {
    clearToken();
    setAuthed(false);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0a101e] flex items-center justify-center">
        <Loader2 className="w-7 h-7 text-yellow-400 animate-spin" />
      </div>
    );
  }

  return authed ? <Dashboard onLogout={logout} /> : <LoginScreen onSuccess={() => setAuthed(true)} />;
};

export default AdminPage;
